import React, { useEffect, useState } from 'react';
import { MapPin, Download, ChevronDown, Sparkles } from 'lucide-react';

const Hero = ({ data, stats }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToAbout = () => {
    document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDownloadCV = () => {
    window.open(data.resumePdf, '_blank');
  };

  return (
    <section id="hero" className="min-h-screen bg-[#0a1628] flex items-center relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Gradient Orbs */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#d4af37]/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#1a2744] rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#d4af37]/3 rounded-full blur-3xl" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="h-full w-full" style={{
            backgroundImage: 'linear-gradient(#d4af37 1px, transparent 1px), linear-gradient(90deg, #d4af37 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        {/* Floating Particles */}
        <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-[#d4af37] rounded-full animate-float" style={{ animationDelay: '0s' }} />
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-[#d4af37] rounded-full animate-float" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-[#d4af37] rounded-full animate-float" style={{ animationDelay: '1s' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 relative z-10 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left Content - 7 columns */}
          <div className={`lg:col-span-7 space-y-8 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 border border-[#d4af37]/30 bg-[#d4af37]/5 px-4 py-2 rounded-full">
              <Sparkles size={14} className="text-[#d4af37]" />
              <span className="text-[#d4af37] text-sm font-medium tracking-wide">Open to Opportunities</span>
            </div>

            {/* Profile Image - Mobile Only */}
            <div className="lg:hidden flex justify-center">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-br from-[#d4af37] to-[#b8962e] rounded-full opacity-30 blur-md" />
                <img 
                  src={data.profileImage} 
                  alt="Vaibhav Bhore"
                  className="relative w-40 h-40 rounded-full object-cover border-4 border-[#d4af37]/30"
                />
              </div>
            </div>

            {/* Name - Large Display */}
            <div className="space-y-2">
              <p className="text-[#94a3b8] text-lg tracking-widest uppercase">Hello, I'm</p>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-none">
                Vaibhav
                <span className="block text-[#d4af37]">Bhore</span>
              </h1>
            </div>

            {/* Title */}
            <div className="space-y-4">
              <p className="text-xl md:text-2xl lg:text-3xl text-white font-light">
                {data.title}
              </p>
              <p className="text-base md:text-lg text-[#94a3b8] leading-relaxed max-w-xl">
                {data.subtitle}
              </p>
            </div>

            {/* Location */}
            <div className="flex items-center gap-3 text-[#64748b]">
              <MapPin size={18} className="text-[#d4af37]" />
              <span className="text-sm tracking-wide">{data.location}</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group relative inline-flex items-center justify-center gap-2 bg-[#d4af37] text-[#0a1628] px-8 py-4 font-semibold text-base overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-[#d4af37]/25"
              >
                <span className="relative z-10">Get In Touch</span>
                <span className="absolute inset-0 bg-[#e8c547] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </a>
              <button
                onClick={handleDownloadCV}
                className="group inline-flex items-center justify-center gap-2 border-2 border-[#d4af37] text-[#d4af37] px-8 py-4 font-semibold text-base hover:bg-[#d4af37] hover:text-[#0a1628] transition-all duration-300"
              >
                <Download size={18} className="group-hover:animate-bounce" />
                Download CV
              </button>
            </div>
          </div>

          {/* Right Content - 5 columns - Profile Image & Stats */}
          <div className={`lg:col-span-5 ${isVisible ? 'animate-fade-up delay-300' : 'opacity-0'}`}>
            <div className="relative">
              {/* Profile Image - Desktop */}
              <div className="hidden lg:block mb-8">
                <div className="relative mx-auto w-fit">
                  {/* Decorative frame */}
                  <div className="absolute -inset-3 bg-gradient-to-br from-[#d4af37] via-[#e8c547] to-[#d4af37] rounded-2xl opacity-20 blur" />
                  <div className="absolute -inset-1 border-2 border-[#d4af37]/30 rounded-2xl" />
                  <img 
                    src={data.profileImage} 
                    alt="Vaibhav Bhore"
                    className="relative w-64 h-64 rounded-2xl object-cover border-2 border-[#d4af37]/50"
                  />
                  {/* Status indicator */}
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#1a2744] border border-[#d4af37]/30 px-4 py-1.5 rounded-full">
                    <span className="text-[#d4af37] text-xs font-semibold tracking-wider flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Available for hire
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats Card */}
              <div className="relative bg-[#1a2744] rounded-2xl p-6 border border-[#d4af37]/20">
                {/* Quote */}
                <div className="mb-6">
                  <p className="text-[#94a3b8] text-sm md:text-base leading-relaxed italic">
                    "{data.headline}"
                  </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {stats.map((stat, index) => (
                    <div 
                      key={index} 
                      className="text-center p-4 bg-[#0a1628] rounded-xl border border-[#d4af37]/10 hover:border-[#d4af37]/30 transition-colors duration-300"
                    >
                      <div className="text-2xl md:text-3xl font-bold text-[#d4af37] mb-1">
                        {stat.value}
                      </div>
                      <div className="text-[10px] md:text-xs text-[#64748b] font-medium uppercase tracking-wider">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden lg:block">
          <button
            onClick={scrollToAbout}
            className="flex flex-col items-center gap-3 text-[#64748b] hover:text-[#d4af37] transition-colors group"
          >
            <span className="text-xs uppercase tracking-[0.2em] font-medium">Explore</span>
            <ChevronDown size={24} className="animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
